import React, { useEffect, useState } from 'react';
import { useUIStore } from '../../../apps/bim-catastro/stores';

interface NotificationCenterProps {
  className?: string;
  style?: React.CSSProperties;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ 
  className = '', 
  style = {} 
}) => {
  const { notifications, removeNotification } = useUIStore();
  const [visibleNotifications, setVisibleNotifications] = useState<string[]>([]);

  // Mostrar notificaciones con animación
  useEffect(() => {
    notifications.forEach(notification => {
      if (!visibleNotifications.includes(notification.id)) {
        setVisibleNotifications(prev => [...prev, notification.id]);
        
        // Auto-remover después del tiempo especificado
        const duration = notification.duration || 5000;
        setTimeout(() => {
          removeNotification(notification.id);
          setVisibleNotifications(prev => prev.filter(id => id !== notification.id));
        }, duration);
      }
    });
  }, [notifications, removeNotification, visibleNotifications]);

  const getNotificationStyle = (type: string) => {
    const baseStyle = {
      padding: '12px 16px',
      marginBottom: '8px',
      borderRadius: '4px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      maxWidth: '400px',
      animation: 'slideIn 0.3s ease-out',
      position: 'relative' as const,
      overflow: 'hidden'
    };

    switch (type) {
      case 'success':
        return {
          ...baseStyle,
          background: '#d4edda',
          border: '1px solid #c3e6cb',
          color: '#155724'
        };
      case 'error':
        return {
          ...baseStyle,
          background: '#f8d7da',
          border: '1px solid #f5c6cb',
          color: '#721c24'
        };
      case 'warning':
        return {
          ...baseStyle,
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          color: '#856404'
        };
      case 'info':
      default:
        return {
          ...baseStyle,
          background: '#d1ecf1',
          border: '1px solid #bee5eb',
          color: '#0c5460'
        };
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return (
          <svg style={{ width: '20px', height: '20px', flexShrink: 0 }} viewBox="0 0 24 24" fill="currentColor">
            <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/>
          </svg>
        );
      case 'error':
        return (
          <svg style={{ width: '20px', height: '20px', flexShrink: 0 }} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12,2C13.1,2 14,2.9 14,4C14,5.1 13.1,6 12,6C10.9,6 10,5.1 10,4C10,2.9 10.9,2 12,2M21,9V7L15,1H5C3.89,1 3,1.89 3,3V21A2,2 0 0,0 5,23H19A2,2 0 0,0 21,21V9M19,9H14V4H5V21H19V9Z"/>
          </svg>
        );
      case 'warning':
        return (
          <svg style={{ width: '20px', height: '20px', flexShrink: 0 }} viewBox="0 0 24 24" fill="currentColor">
            <path d="M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
          </svg>
        );
      case 'info':
      default:
        return (
          <svg style={{ width: '20px', height: '20px', flexShrink: 0 }} viewBox="0 0 24 24" fill="currentColor">
            <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
          </svg>
        );
    }
  };

  const handleClose = (notificationId: string) => {
    removeNotification(notificationId);
    setVisibleNotifications(prev => prev.filter(id => id !== notificationId));
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div 
      className={`notification-center ${className}`}
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        ...style
      }}
    >
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes slideOut {
            from {
              transform: translateX(0);
              opacity: 1;
            }
            to {
              transform: translateX(100%);
              opacity: 0;
            }
          }
        `}
      </style>
      
      {notifications.map((notification) => (
        <div
          key={notification.id}
          style={getNotificationStyle(notification.type)}
        >
          {getIcon(notification.type)}
          
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              fontWeight: 'bold', 
              marginBottom: '4px',
              fontSize: '14px'
            }}>
              {notification.title}
            </div>
            <div style={{ 
              fontSize: '13px',
              lineHeight: '1.4'
            }}>
              {notification.message}
            </div>
            <div style={{ 
              fontSize: '11px', 
              opacity: 0.7,
              marginTop: '4px'
            }}>
              {(() => {
                const ts: any = (notification as any).timestamp;
                const date = ts instanceof Date ? ts : new Date(ts);
                return isNaN(date.getTime()) ? '' : date.toLocaleTimeString();
              })()}
            </div>
          </div>
          
          <button
            onClick={() => handleClose(notification.id)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              padding: '4px',
              fontSize: '16px',
              opacity: 0.7,
              flexShrink: 0
            }}
            title="Cerrar"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

export default NotificationCenter;
